import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import GuildDashboard from "./GuildDashboard";
import GuildSetupTour, { requestGuildSetupTour, startGuildSetupTour } from "./GuildSetupTour";
import { GuildWeeklyRelease } from "./GuildWeeklyRelease";
import GuildReadyBar from "./GuildReadyBar";
import type { GuildStage } from "./GuildSetupTour";
import DOMPurify from "dompurify";
import { useConfirm } from "./ConfirmDialog";
import { useToast } from "./Toast";
import { API_URL } from "./config";
import { useTimezone } from "./lib/TimezoneContext";
import { getCurrentResetWeek, getNextResetWeek, localCellToBitIdx } from "./lib/time";
import { DEFAULT_RESET_RULE, ResetRule } from "./lib/time";
import { formatWeekStartShort } from "./lib/regionReset";
import {
  FieldLabel,
  MODAL_FIELD_CLS,
  CrossfadeImage,
  modalShellStyle,
  isLightHex,
} from "./modalChrome";

import { tc, useLegible } from "./theme/legible";
import { DISPLAY } from "./alignmentUi";
import GuildGamesPanel from "./GuildGamesPanel";
import GuildDirectory from "./GuildDirectory";
import GuildRecruitingPanel from "./GuildRecruitingPanel";
import GuildProfile from "./GuildProfile";
// --- CONSTANTS & UTILS ---

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || "";

const CLASS_BG = `${API_URL}/class-backgrounds`;

const CLASS_ART: Record<string, string> = {
  Warrior:      `${CLASS_BG}/Warrior.png`,
  Paladin:      `${CLASS_BG}/Paladin.png`,
  Hunter:       `${CLASS_BG}/hunter.png`,
  Rogue:        `${CLASS_BG}/rogue.png`,
  Priest:       `${CLASS_BG}/priest.png`,
  "Death Knight": `${CLASS_BG}/deathknight.png`,
  Shaman:       `${CLASS_BG}/shaman.png`,
  Mage:         `${CLASS_BG}/mage.png`,
  Warlock:      `${CLASS_BG}/warlock.png`,
  Monk:         `${CLASS_BG}/monk.png`,
  Druid:        `${CLASS_BG}/druid.png`,
  "Demon Hunter": `${CLASS_BG}/demonhunter.png`,
  Evoker:       `${CLASS_BG}/evoker.png`,
};

function getClassArtUrl(classLabel: string | null | undefined): string | null {
  if (!classLabel) return null;
  const norm = classLabel.trim().toLowerCase();
  const key = Object.keys(CLASS_ART).find((k) => k.toLowerCase() === norm);
  return key ? CLASS_ART[key] : null;
}

function sanitizeAbout(html: string): string {
    return DOMPurify.sanitize(String(html || ''), {
      ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div'],
      ALLOWED_ATTR: ['class', 'style'],
    });
  }

// DB Constraint: Faction must be Title Case (Alliance, Horde, Neutral)
function formatFaction(f: string): string | null {
    if (!f || !f.trim()) return null;
    const lower = f.trim().toLowerCase();
    if (lower === "alliance") return "Alliance";
    if (lower === "horde") return "Horde";
    if (lower === "neutral") return "Neutral";
    return f.charAt(0).toUpperCase() + f.slice(1);
}

function getFactionColor(faction: string | null | undefined): string {
  const f = (faction || "").toLowerCase();
  if (f.includes("alliance")) return "#3b82f6"; // blue-500
  if (f.includes("horde")) return "#ef4444"; // red-500
  return "#eab308"; // yellow-500 (Neutral)
}


// Get role color from game fields metadata
function getRoleColor(roleLabel: string, fields: OptionGroup[] | null | undefined): string | null {
  if (!fields || !roleLabel) return null;
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup) return null;
  const target = roleLabel.trim().toLowerCase();
  
  // Match against label OR key (fixes issues with "melee_dps" vs "Melee DPS")
  const opt = roleGroup.options.find((o) => 
    o.label.toLowerCase() === target || 
    (o.key && o.key.toLowerCase() === target)
  );

  if (!opt || !opt.metadata) return null;
  return typeof opt.metadata.role_color === "string" ? opt.metadata.role_color.trim() : null;
}

// Get class color from game fields metadata
function getClassColor(classLabel: string, fields: OptionGroup[] | null | undefined): string | null {
  if (!fields || !classLabel) return null;
  const classGroup = fields.find((g) => g.key === "class");
  if (!classGroup) return null;
  const target = classLabel.trim().toLowerCase();
  const opt = classGroup.options.find((o) => o.label.toLowerCase() === target);
  if (!opt || !opt.metadata) return null;
  return typeof opt.metadata.color === "string" ? opt.metadata.color.trim() : null;
}

// --- WEEK UTILS ---

// Reset-week helpers now come from the shared implementation. These were a
// private copy of the "Wednesday 00:00 UTC" rule, which is EU-shaped and a day
// out for US realms. The default is EU, matching the previous behaviour.


// See src/lib/regionReset.ts: never print the label itself, resolve it first.
function formatWeekLabel(weekStart: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  return formatWeekStartShort(weekStart, rule);
}

function formatWeekRange(weekStart: string): string {
  const s = new Date(weekStart + 'T00:00:00Z');
  const e = new Date(s); e.setUTCDate(s.getUTCDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(s)} – ${fmt(e)}`;
}

// --- PARSE COLOR ---
function getParseColor(score: number): string {
  if (score === 100) return '#e5cc80';
  if (score >= 99) return '#e268a8';
  if (score >= 95) return '#ff8000';
  if (score >= 75) return '#a335ee';
  if (score >= 50) return '#0070dd';
  if (score >= 25) return '#1eff00';
  return '#9d9d9d';
}

// --- TYPES ---

type RosterMember = {
  characterId: string;
  name: string;
  gameId: string;
  ilvl: number | null;
  metadata: any;
  guildRank: string | null;
  isOfficer: boolean;
  joinedAt: string;
  userId?: string;
  userDisplayName?: string;
  options?: { group_id: number; option_id: number }[];
  userAvailability?: string;
  isConfirmed?: boolean;
  availabilityFromWeek?: string;
  userDiscordId?: string;
  totalRaidCount?: number;
};

  type Guild = {
    id: string;
    gameId: string;
    ownerUserId?: string;
    name: string;
    tag: string;
    slug: string;
    serverName?: string | null;
    faction?: string | null;
    region?: string | null;
    aboutRaw?: string | null;
    contactInfo?: string | null;
    scheduleSummary?: string | null;
    logoKey?: string | null;
    logoUrl?: string | null;
    inviteCode?: string | null;
    isOwner?: boolean;
    isOfficer?: boolean;
    recruiting?: boolean;
    roster?: RosterMember[];
    memberCount?: number;
    metadata?: Record<string, any>;
  };

  type Game = { id: string; code: string; name: string };
type Option = { id: number; key: string; label: string; value_key?: string; metadata?: any };
type OptionGroup = { id: number; key: string; label: string; multi_select: boolean; options: Option[] };
// Shape returned by GET /characters (fields we use here). Note the API returns
// `gameId` (camelCase), not `game_id`.
type Character = {
  id: string;
  name: string;
  gameId?: string;
  guildId?: string | null;
  guildName?: string | null;
  className?: string | null;
  metadata?: any;
  options?: { group_id: number; option_id: number }[];
};

// --- AVAILABILITY GRID UTILS (module-scope so they survive across renders) ---

// Display order matches roster / squad overlap: local Wed → Tue rows, columns = local half-hours.
const AVAIL_GRID_DISPLAY_DAYS = ["Wed", "Thu", "Fri", "Sat", "Sun", "Mon", "Tue"] as const;
const AVAIL_GRID_DAY_LOCAL_MON0 = [2, 3, 4, 5, 6, 0, 1] as const;

function availBitOn(bits: string | undefined, idx: number): boolean {
  return !!bits && idx >= 0 && idx < bits.length && bits[idx] === "1";
}

// Cache day×slot → bitIdx per (weekIso, userTz). Without this the dual-week
// grid runs `localCellToBitIdx` (which spins up a Luxon DateTime + zone
// conversion) 672+ times *per card per render*. With it we do 336 once.
const _weekBitIdxCache = new Map<string, Int16Array>();
function getWeekBitIndices(weekIso: string, userTz: string): Int16Array {
  const key = `${weekIso}|${userTz}`;
  const cached = _weekBitIdxCache.get(key);
  if (cached) return cached;
  const arr = new Int16Array(7 * 48);
  for (let d = 0; d < 7; d++) {
    const dayLocalMon0 = AVAIL_GRID_DAY_LOCAL_MON0[d];
    for (let s = 0; s < 48; s++) {
      arr[d * 48 + s] = localCellToBitIdx(weekIso, dayLocalMon0, s, userTz);
    }
  }
  _weekBitIdxCache.set(key, arr);
  return arr;
}

const _formatTimeLabel = (slotIdx: number) =>
  `${Math.floor(slotIdx / 2).toString().padStart(2, "0")}:${slotIdx % 2 === 0 ? "00" : "30"}`;

// --- COMPONENT: Dual-Week Availability Grid (stacked bars) ---
// Module-scope + memoized so expand/collapse on one card doesn't remount this
// subtree for every other rendered card.
const DualWeekAvailGrid = React.memo(function DualWeekAvailGrid({
  currentWeekAvail,
  nextWeekAvail,
  userTz,
  resetWeekdayIso = 3,
}: {
  currentWeekAvail?: string;
  nextWeekAvail?: string;
  userTz: string;
  /** The guild's reset weekday: 3 = Wednesday (EU), 2 = Tuesday (US). */
  resetWeekdayIso?: number;
}) {
  const hasCurrent = !!currentWeekAvail && currentWeekAvail.length === 336;
  const hasNext = !!nextWeekAvail && nextWeekAvail.length === 336;
  if (!hasCurrent && !hasNext) {
    return (
      <div className="flex items-center justify-center h-12 bg-[var(--c-bg-veil)] rounded-lg border border-[var(--c-line-soft)] text-[var(--c-ink-4)] text-xs italic">
        No availability data
      </div>
    );
  }
  const guildRule: ResetRule = { weekday: resetWeekdayIso, hour: 0, tz: 'UTC' };
  const weekCurrentIso = getCurrentResetWeek(guildRule);
  const weekNextIso = getNextResetWeek(guildRule);
  const curIdx = getWeekBitIndices(weekCurrentIso, userTz);
  const nextIdx = getWeekBitIndices(weekNextIso, userTz);

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-4 px-1">
        <span className="text-xs font-bold uppercase text-[var(--c-ink-4)] tracking-wider">Availability</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-sm bg-cyan-500"></div><span className="text-[10px] text-[var(--c-ink-3)]">Current ({formatWeekLabel(weekCurrentIso, guildRule)})</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-sm bg-amber-500"></div><span className="text-[10px] text-[var(--c-ink-3)]">Next ({formatWeekLabel(weekNextIso, guildRule)})</span></div>
        </div>
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-line-soft)] rounded-lg p-3 space-y-0.5">
        <div className="flex pl-8 text-[9px] text-[var(--c-ink-4)] font-mono justify-between select-none px-0.5 mb-1"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
        {AVAIL_GRID_DISPLAY_DAYS.map((day, displayIdx) => {
          const base = displayIdx * 48;
          let active = false;
          for (let s = 0; s < 48; s++) {
            if (
              (hasCurrent && availBitOn(currentWeekAvail, curIdx[base + s])) ||
              (hasNext && availBitOn(nextWeekAvail, nextIdx[base + s]))
            ) { active = true; break; }
          }
          return (
            <div key={day} className="flex items-center gap-1">
              <div className={`w-7 text-[10px] font-bold ${active ? 'text-[var(--c-ink-2)]' : 'text-[var(--c-ink-4)]'}`}>{day}</div>
              <div className="flex-1 flex flex-col gap-[1px]">
                <div className="h-[5px] bg-[var(--c-bg-veil)] rounded-sm overflow-hidden flex">
                  {hasCurrent && Array.from({ length: 48 }).map((_, s) => {
                    const on = availBitOn(currentWeekAvail, curIdx[base + s]);
                    return (
                      <div
                        key={s}
                        className="flex-1"
                        style={{ backgroundColor: on ? "#06b6d4" : "transparent" }}
                        title={on ? `${day} ${_formatTimeLabel(s)} — Current` : undefined}
                      />
                    );
                  })}
                </div>
                <div className="h-[5px] bg-[var(--c-bg-veil)] rounded-sm overflow-hidden flex">
                  {hasNext && Array.from({ length: 48 }).map((_, s) => {
                    const on = availBitOn(nextWeekAvail, nextIdx[base + s]);
                    return (
                      <div
                        key={s}
                        className="flex-1"
                        style={{ backgroundColor: on ? "#f59e0b" : "transparent" }}
                        title={on ? `${day} ${_formatTimeLabel(s)} — Next` : undefined}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// --- COMPONENT: Rallify Rank Badge ---
const RallifyRankBadge = React.memo(function RallifyRankBadge({ tier, color, glowColor, rank }: { tier: string; color: string; glowColor: string; count: number; rank: number }) {
  if (tier === 'Unranked') return (
    <div className="flex flex-col items-center w-12">
      <div className="w-12 h-9 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] flex items-center justify-center text-sm font-bold text-[var(--c-ink-4)]">—</div>
      <div className="text-[8px] font-bold uppercase tracking-wider mt-0.5 text-[var(--c-ink-4)]">rank</div>
    </div>
  );
  return (
    <div className="flex flex-col items-center w-12">
      <div className="w-12 h-9 rounded-lg border flex items-center justify-center text-lg font-black tabular-nums"
        style={{ color, borderColor: `${color}40`, backgroundColor: `${color}12`, boxShadow: `0 0 12px ${glowColor}` }}>
        {rank}
      </div>
      <div className="text-[8px] font-bold uppercase tracking-wider mt-0.5" style={{ color: `${tc(color)}99` }}>{tier}</div>
    </div>
  );
});

// --- COMPONENTS ---

const RichTextEditor = React.memo(({ value, onChange }: { value: string; onChange: (html: string) => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ""; }, [value]);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onInput={() => onChange(sanitizeAbout(ref.current?.innerHTML || ""))}
      className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-3 py-2.5 text-sm text-[var(--c-ink)] min-h-[150px] max-h-[280px] overflow-y-auto outline-none focus:ring-1 focus:ring-[color:var(--modal-accent)] focus:border-[color:var(--modal-accent)]"
    />
  );
}, () => true);

// --- COMPONENT: Request to Join (web application from the directory) ---
// Available to any authed user (member or not) on a guild's detail view and via
// the overview shortcut modal. Lets a player apply with one or more of their
// existing characters. Only characters on the guild's server AND faction are
// eligible; characters already in another guild are allowed (approval moves
// them). Files pending guild_join_request rows that surface in the officer
// "Requests" tab. Renders nothing when the user already belongs here and has no
// other eligible character to add.
function RequestToJoinPanel({ guild, characters, fields, className = "", hideWhenEmpty = true }: {
  guild: Guild;
  characters: Character[];
  fields: OptionGroup[];
  className?: string;
  hideWhenEmpty?: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<
    { characterId: string; name?: string; ok: boolean; error?: string; message?: string; switching?: boolean }[] | null
  >(null);
  // characterId -> pending requestId for THIS guild (drives the ⏳ + Withdraw state)
  const [pending, setPending] = useState<Record<string, string>>({});

  const loadPending = useCallback(() => {
    fetch("/me/join-requests", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: any[]) => {
        const map: Record<string, string> = {};
        (Array.isArray(rows) ? rows : [])
          .filter((r) => r.guildId === guild.id && r.status === "pending")
          .forEach((r) => { if (r.characterId) map[r.characterId] = r.id; });
        setPending(map);
      })
      .catch(() => {});
  }, [guild.id]);
  useEffect(() => { loadPending(); }, [loadPending]);

  // Resolve a character's selected option label for a given group (realm /
  // faction) from the character's option ids + the game's field definitions.
  const labelForGroup = useCallback((c: Character, groupKey: string): string | null => {
    const group = fields.find((g) => g.key === groupKey);
    if (!group || !c.options) return null;
    const sel = c.options.find((o) => Number(o.group_id) === Number(group.id));
    if (!sel) return null;
    const opt = group.options.find((o) => Number(o.id) === Number(sel.option_id));
    return opt?.label || null;
  }, [fields]);

  const normRealm = (s: string) => s.toLowerCase().replace(/\s*\((?:pvp|pve|rp|rp-pvp)\)\s*/gi, "").trim();

  // Candidates = same game + not already a member of THIS guild. Each carries an
  // eligibility verdict (server + faction). When fields aren't loaded yet we
  // can't verify locally, so we let it through and rely on the server's check.
  const candidates = useMemo(() => {
    return characters
      .filter((c) => c.gameId === guild.gameId && c.guildId !== guild.id)
      .map((c) => {
        let ineligible: string | null = null;
        if (guild.serverName) {
          const realm = labelForGroup(c, "realm");
          if (realm && normRealm(realm) !== normRealm(guild.serverName)) ineligible = "Different realm";
        }
        if (!ineligible && guild.faction && guild.faction.trim().toLowerCase() !== "neutral") {
          const faction = labelForGroup(c, "faction");
          if (faction && faction.trim().toLowerCase() !== guild.faction.trim().toLowerCase()) ineligible = "Different faction";
        }
        return { c, ineligible };
      });
  }, [characters, guild.gameId, guild.id, guild.serverName, guild.faction, labelForGroup]);

  const isMemberHere = useMemo(
    () => characters.some((c) => c.guildId === guild.id),
    [characters, guild.id]
  );
  const hasSameGameChar = useMemo(
    () => characters.some((c) => c.gameId === guild.gameId),
    [characters, guild.gameId]
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true); setError(null); setResults(null);
    try {
      const res = await fetch(`/guilds/${guild.id}/request-join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ characterIds: Array.from(selected) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.error === "guild_not_found" ? "This guild is no longer available." :
          data.error === "recruiting_closed" ? (data.message || "This guild has closed recruiting.") :
          "Could not send your request. Try again."
        );
      } else {
        setResults(data.results || []);
        setSelected(new Set());
        loadPending();
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (characterId: string) => {
    const reqId = pending[characterId];
    if (!reqId) return;
    try {
      await fetch(`/guilds/join-requests/${reqId}/cancel`, { method: "POST", credentials: "include" });
    } catch {}
    loadPending();
  };

  const errorLabel = (r: { error?: string; message?: string }) => {
    switch (r.error) {
      case "already_pending": return "Already requested";
      case "already_member": return "Already a member";
      case "realm_mismatch": return r.message || "Realm does not match";
      case "faction_mismatch": return r.message || "Faction does not match";
      case "wrong_game": return "Different game";
      default: return "Could not apply";
    }
  };

  const pendingCount = Object.keys(pending).length;

  // In the hero, members with nothing new to add see no panel at all (keeps it
  // clean). In the modal (hideWhenEmpty=false) we fall through to a message.
  if (candidates.length === 0 && isMemberHere && hideWhenEmpty) return null;

  const rootClass = `p-4 rounded-xl bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] border border-cyan-500/15 space-y-3 min-w-0 ${className}`;

  return (
    <div className={rootClass}>
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-bold text-cyan-700 dark:text-cyan-400 uppercase tracking-widest">Request to Join</h3>
        {pendingCount > 0 && <span className="text-[10px] text-amber-700/80 dark:text-amber-300/80">{pendingCount} pending</span>}
      </div>

      {candidates.length === 0 ? (
        <p className="text-[11px] text-[var(--c-ink-3)] leading-relaxed">
          {hasSameGameChar
            ? "Your characters for this game are already in this guild."
            : "You have no characters in this guild's game yet. Create one to apply."}
        </p>
      ) : (
        <>
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {candidates.map(({ c, ineligible }) => {
              const isPending = !!pending[c.id];
              const color = getClassColor(c.className || c.metadata?.class_name || "", fields) || "#8ab4f8";
              const isSel = selected.has(c.id);
              const locked = isPending || !!ineligible;
              return (
                <div key={c.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${
                  ineligible ? "border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] opacity-60"
                    : isPending ? "border-amber-500/20 bg-amber-500/[0.04]"
                    : isSel ? "border-cyan-500/40 bg-cyan-500/[0.08]"
                    : "border-[var(--c-line)] bg-[var(--c-bg-veil)] hover:border-[var(--c-line-strong)]"
                }`}>
                  {isPending ? (
                    <span className="grid h-4 w-4 place-items-center text-[10px]">⏳</span>
                  ) : ineligible ? (
                    <span className="grid h-4 w-4 place-items-center text-[10px] text-[var(--c-ink-4)]">✕</span>
                  ) : (
                    <span
                      role="checkbox"
                      aria-checked={isSel}
                      onClick={() => toggle(c.id)}
                      className={`h-4 w-4 rounded border flex items-center justify-center transition-colors cursor-pointer ${
                        isSel ? "bg-cyan-500 border-cyan-500" : "border-[var(--c-line-strong)] hover:border-[var(--c-ink-4)]"
                      }`}
                    >
                      {isSel && <span className="w-2 h-2 bg-white rounded-sm" />}
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => !locked && toggle(c.id)}
                    className="flex-1 min-w-0 text-left disabled:cursor-default"
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs font-semibold text-[var(--c-ink)] truncate">{c.name}</span>
                    </div>
                    <div className="text-[10px] truncate pl-3">
                      {ineligible ? <span className="text-red-700/70 dark:text-red-300/70">{ineligible}</span>
                        : isPending ? <span className="text-[var(--c-ink-3)]">Requested · awaiting officer</span>
                        : c.guildId ? <span className="text-amber-700/70 dark:text-amber-300/70">Switch from {c.guildName || "current guild"}</span>
                        : <span className="text-[var(--c-ink-3)]">Guildless</span>}
                    </div>
                  </button>
                  {isPending && (
                    <button
                      type="button"
                      onClick={() => withdraw(c.id)}
                      className="shrink-0 rounded-md border border-[var(--c-line)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-3)] hover:border-red-400/30 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {error && <div className="text-[11px] text-red-700 dark:text-red-300">{error}</div>}

          {results && (
            <div className="space-y-1">
              {results.map((r) => (
                <div key={r.characterId} className={`text-[11px] ${r.ok ? "text-emerald-700/90 dark:text-emerald-300/90" : "text-[var(--c-ink-3)]"}`}>
                  {r.ok
                    ? <>✓ {r.name} · request sent{r.switching ? " (switch)" : ""}</>
                    : <>· {r.name || "Character"}: {errorLabel(r)}</>}
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={submitting || selected.size === 0}
            className="w-full px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold transition-colors"
          >
            {submitting ? "Sending…" : selected.size > 0 ? `Request to Join (${selected.size})` : "Request to Join"}
          </button>
          <p className="text-[9px] text-[var(--c-ink-4)] leading-relaxed">
            Same server and faction only. An officer reviews each request; switching characters move here from their current guild once accepted.
          </p>
        </>
      )}
    </div>
  );
}

export type GuildFormDraft = {
  gameId: string;
  name: string;
  tag: string;
  region: string;
  serverName: string;
  faction: string;
  aboutRaw: string;
  contactInfo: string;
  scheduleSummary: string;
  recruiting: boolean;
};

function emptyGuildDraft(gameId: string): GuildFormDraft {
  return {
    gameId,
    name: "",
    tag: "",
    region: "",
    serverName: "",
    faction: "",
    aboutRaw: "",
    contactInfo: "",
    scheduleSummary: "",
    recruiting: false,
  };
}

function draftFromGuild(g: Guild): GuildFormDraft {
  return {
    gameId: g.gameId || "",
    name: g.name || "",
    tag: g.tag || "",
    region: g.region || "",
    serverName: g.serverName || "",
    faction: g.faction || "",
    aboutRaw: g.aboutRaw || "",
    contactInfo: g.contactInfo || "",
    scheduleSummary: g.scheduleSummary || "",
    recruiting: g.recruiting === true || g.metadata?.recruiting === true,
  };
}

function resolveGuildLogoUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `${AUTH_BASE_URL}${url}`;
}

function GuildFormModal({
  mode,
  games,
  fieldsByGame,
  onFieldsLoaded,
  guild,
  onClose,
  onSubmit,
  onDelete,
}: {
  mode: "create" | "edit";
  games: Game[];
  fieldsByGame: Record<string, OptionGroup[]>;
  onFieldsLoaded: (gameId: string, data: OptionGroup[]) => void;
  guild: Guild | null;
  onClose: () => void;
  onSubmit: (values: GuildFormDraft & { logoFile: File | null }) => Promise<string | null>;
  onDelete?: () => Promise<string | null>;
}) {
  const [draft, setDraft] = useState<GuildFormDraft>(() =>
    guild ? draftFromGuild(guild) : emptyGuildDraft(games[0]?.id || "")
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(() =>
    resolveGuildLogoUrl(guild?.logoUrl)
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const [headerName, setHeaderName] = useState(draft.name);
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const groups = fieldsByGame[draft.gameId] || [];
  const getOptionsForGroup = (key: string) => {
    const group = groups.find(
      (g) => g.key === key || g.key === key + "s" || g.label.toLowerCase() === key
    );
    return group?.options || [];
  };

  const regionOpts = getOptionsForGroup("region");
  const realmOpts = getOptionsForGroup("realm");
  const factionOpts = getOptionsForGroup("faction");

  const selectedRegion = regionOpts.find((o) => o.label === draft.region);
  const selectedRegionKey = selectedRegion?.key || selectedRegion?.label?.toLowerCase() || null;
  const visibleRealms = !selectedRegionKey
    ? []
    : realmOpts.filter((o) => {
        const allowed = o.metadata?.allowed_region_keys;
        return !allowed || allowed.includes(selectedRegionKey);
      });

  const hasFaction = Boolean(draft.faction);
  const accent = hasFaction ? getFactionColor(draft.faction) : "#A3A3A3";
  const shellAccent = hasFaction ? accent : "#27272a";

  useEffect(() => {
    if (!draft.gameId || fieldsByGame[draft.gameId]) return;
    let cancelled = false;
    fetch(`/games/${draft.gameId}/fields`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) onFieldsLoaded(draft.gameId, data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [draft.gameId, fieldsByGame, onFieldsLoaded]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(resolveGuildLogoUrl(guild?.logoUrl));
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile, guild?.logoUrl]);

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    };
  }, []);

  const patch = (partial: Partial<GuildFormDraft>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  const onNameInput = () => {
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      setHeaderName(nameRef.current?.value || "");
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const name = (nameRef.current?.value || draft.name).trim();
    if (!draft.gameId) {
      setFormError("Game is required.");
      return;
    }
    if (!name) {
      setFormError("Guild Name is required.");
      return;
    }
    if (!draft.tag.trim()) {
      setFormError("Guild Tag is required.");
      return;
    }
    setIsSubmitting(true);
    try {
      const err = await onSubmit({ ...draft, name, tag: draft.tag.trim(), logoFile });
      if (err) setFormError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!onDelete) return;
    setFormError(null);
    setIsSubmitting(true);
    try {
      const err = await onDelete();
      if (err) setFormError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleText = headerName.trim() || (mode === "create" ? "Create Guild" : "Edit Guild");
  const sublineParts = [
    draft.tag.trim() ? `[${draft.tag.trim()}]` : null,
    draft.faction || null,
    draft.serverName || null,
  ].filter(Boolean);
  const ctaText = isSubmitting ? "Saving..." : mode === "create" ? "Create Guild" : "Save Changes";
  const initial = headerName.trim().charAt(0).toUpperCase();
  const iconLive = hasFaction || Boolean(logoPreview);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={onClose} />
      <div
        className="char-modal-shell relative w-full max-w-4xl bg-[var(--c-surface)] border rounded-xl flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200"
        style={modalShellStyle(shellAccent)}
      >
        <div className="lfm-dark relative h-40 shrink-0 overflow-hidden">
          <div className="char-modal-splash-idle absolute inset-0 z-0" />
          <CrossfadeImage
            src={logoPreview}
            className="absolute inset-0 z-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-cover [object-position:center_18%] scale-110"
          />
          <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#09090b] via-[#09090b]/35 to-black/10" />
          <div className="relative z-[2] h-full flex items-end gap-3 p-5">
            <div
              className="char-modal-tone relative w-14 h-14 rounded-xl border overflow-hidden shrink-0 bg-[var(--c-surface)] flex items-center justify-center"
              style={
                iconLive
                  ? { borderColor: `${accent}66`, boxShadow: `0 0 18px ${accent}44` }
                  : { borderColor: "rgba(255,255,255,0.1)", boxShadow: "none" }
              }
            >
              {logoPreview ? (
                <CrossfadeImage src={logoPreview} className="absolute inset-0 h-full w-full" />
              ) : initial ? (
                <span className="text-xl font-black" style={{ color: tc(hasFaction ? accent : "var(--c-ink-3)") }}>
                  {initial}
                </span>
              ) : null}
            </div>
            <div className="min-w-0 flex-1 pb-0.5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--c-ink-3)]">
                {mode === "create" ? "New Guild" : "Edit Guild"}
              </div>
              <h3
                className="char-modal-tone text-xl font-bold truncate leading-tight"
                style={{
                  color: tc(hasFaction ? accent : "var(--c-ink)"),
                  textShadow: hasFaction ? "0 1px 10px rgba(0,0,0,0.85)" : "none",
                }}
              >
                {titleText}
              </h3>
              <div
                className="text-xs truncate mt-0.5"
                style={{
                  color: tc(hasFaction ? "var(--c-ink-2)" : "var(--c-ink-3)"),
                  textShadow: hasFaction ? "0 1px 8px rgba(0,0,0,0.8)" : "none",
                }}
              >
                {sublineParts.length ? sublineParts.join(" · ") : "Pick a faction to preview the sheet."}
              </div>
            </div>
            <button onClick={onClose} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] self-start mt-1">
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {formError && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-800 dark:text-red-200 text-sm">
              {formError}
            </div>
          )}

          {/* Two columns of short fields on desktop (identity left, server +
              recruiting right) with the About editor full width underneath, so
              the sheet fits without scrolling. One column on narrow screens. */}
          <form id="guildForm" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div className="space-y-5">
{mode === "create" && (
            <div className="space-y-1.5">
              <FieldLabel>Game</FieldLabel>
              <select
                value={draft.gameId}
                onChange={(e) => patch({ gameId: e.target.value })}
                className={MODAL_FIELD_CLS}
              >
                <option value="">Select Game...</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
)}

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-1.5">
                <FieldLabel>Guild Name</FieldLabel>
                <input
                  ref={nameRef}
                  defaultValue={draft.name}
                  onInput={onNameInput}
                  className={`${MODAL_FIELD_CLS} text-base font-semibold`}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Tag</FieldLabel>
                <input
                  value={draft.tag}
                  onChange={(e) => patch({ tag: e.target.value })}
                  className={MODAL_FIELD_CLS}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Emblem</FieldLabel>
              <label
                className="char-modal-tone flex items-center gap-3 w-full rounded-xl border cursor-pointer p-3"
                style={
                  logoPreview
                    ? {
                        borderColor: `${accent}66`,
                        backgroundColor: `${accent}14`,
                        boxShadow: `0 0 18px ${accent}18`,
                      }
                    : { borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.03)" }
                }
              >
                <div
                  className="char-modal-tone relative w-12 h-12 rounded-lg overflow-hidden border shrink-0 bg-[var(--c-surface)] flex items-center justify-center"
                  style={{ borderColor: logoPreview ? `${accent}55` : "rgba(255,255,255,0.1)" }}
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="w-full h-full object-cover" />
                  ) : initial ? (
                    <span className="text-lg font-black" style={{ color: tc(hasFaction ? accent : "var(--c-ink-4)") }}>
                      {initial}
                    </span>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[10px] uppercase tracking-widest font-bold"
                    style={{ color: tc(logoPreview ? accent : "var(--c-ink-3)") }}
                  >
                    {logoFile ? logoFile.name : logoPreview ? "Emblem" : "Upload emblem"}
                  </div>
                  <div className="text-[11px] text-[var(--c-ink-3)] truncate mt-0.5">
                    {logoPreview ? "Shown on your guild sheet and in the directory." : "Fills the header once an image is chosen."}
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
            </div>

            <div className="space-y-5">
{mode === "create" ? (
<>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel>Region</FieldLabel>
                {regionOpts.length > 0 ? (
                  <select
                    value={draft.region}
                    onChange={(e) => patch({ region: e.target.value, serverName: "" })}
                    className={MODAL_FIELD_CLS}
                  >
                    <option value="">Select Region...</option>
                    {regionOpts.map((o) => (
                      <option key={o.id} value={o.label}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={draft.region}
                    onChange={(e) => patch({ region: e.target.value, serverName: "" })}
                    className={MODAL_FIELD_CLS}
                    placeholder="Enter Region"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Realm</FieldLabel>
                <select
                  value={draft.serverName}
                  disabled={!selectedRegionKey}
                  onChange={(e) => patch({ serverName: e.target.value })}
                  className={MODAL_FIELD_CLS}
                >
                  <option value="">{selectedRegionKey ? "Select Realm..." : "Select Region first..."}</option>
                  {visibleRealms.map((o) => (
                    <option key={o.id} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Faction</FieldLabel>
              {factionOpts.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {factionOpts.map((opt) => {
                    const color = getFactionColor(opt.label);
                    const selected = draft.faction === opt.label;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => patch({ faction: opt.label })}
                        className="char-modal-tone rounded-xl py-3 text-[11px] font-bold uppercase tracking-widest border"
                        style={
                          selected
                            ? {
                                borderColor: color,
                                backgroundColor: `${color}22`,
                                color,
                                boxShadow: `0 0 18px ${color}33`,
                              }
                            : { borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input
                  value={draft.faction}
                  onChange={(e) => patch({ faction: e.target.value })}
                  className={MODAL_FIELD_CLS}
                  placeholder="Enter Faction"
                />
              )}
            </div>
</>
) : (
            // Realm, faction and region belong to each game the guild plays and
            // are edited in the Games panel on the guild page.
            <p className="rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] px-4 py-3 text-xs text-[var(--c-ink-2)]">
              Games, realms and factions are set per game in the <span className="font-bold text-[var(--c-ink)]">Games</span> panel on the guild page.
            </p>
)}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel>Schedule</FieldLabel>
                <input
                  value={draft.scheduleSummary}
                  onChange={(e) => patch({ scheduleSummary: e.target.value })}
                  className={MODAL_FIELD_CLS}
                  placeholder="e.g. Tue/Thu 20:00"
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Contact</FieldLabel>
                <input
                  value={draft.contactInfo}
                  onChange={(e) => patch({ contactInfo: e.target.value })}
                  className={MODAL_FIELD_CLS}
                  placeholder="Discord link / User"
                />
              </div>
            </div>

            <label
              className="relative flex items-center gap-3 cursor-pointer group/rec pt-1"
              role="checkbox"
              aria-checked={draft.recruiting}
              aria-label="Accept join requests from the guild directory"
              onClick={(e) => {
                e.preventDefault();
                patch({ recruiting: !draft.recruiting });
              }}
            >
              <div
                className="char-modal-tone w-5 h-5 rounded border flex items-center justify-center flex-shrink-0"
                style={
                  draft.recruiting
                    ? { backgroundColor: accent, borderColor: accent }
                    : { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.28)" }
                }
              >
                {draft.recruiting && (
                  <svg className="w-3 h-3" fill="none" stroke={isLightHex(accent) ? "#111" : "#fff"} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span
                className="char-modal-tone text-xs font-bold uppercase tracking-widest"
                style={
                  draft.recruiting
                    ? { color: accent, fontSize: 13, textShadow: `0 0 12px ${accent}66` }
                    : { color: "rgba(255,255,255,0.8)" }
                }
              >
                Accept join requests
              </span>
              <div className="absolute left-8 bottom-full mb-2 px-3 py-2 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] w-64 opacity-0 pointer-events-none group-hover/rec:opacity-100 z-50 shadow-xl normal-case tracking-normal font-normal leading-relaxed">
                Directory Request to Join only. Discord and invite-code joins unchanged.
              </div>
            </label>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <FieldLabel>About</FieldLabel>
              <RichTextEditor value={draft.aboutRaw} onChange={(html) => patch({ aboutRaw: html })} />
            </div>
          </form>
        </div>

        <div className="p-5 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)] flex gap-3">
          {mode === "edit" && onDelete && (
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isSubmitting}
              className="px-4 py-3 bg-red-500/10 dark:bg-red-900/20 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-red-900/40 transition-all disabled:opacity-50"
            >
              Disband
            </button>
          )}
          <button
            type="submit"
            form="guildForm"
            disabled={isSubmitting}
            className="char-modal-tone flex-1 w-full py-3 font-bold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: accent, color: isLightHex(accent) ? "#111" : "#fff" }}
          >
            {ctaText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuildsPage() {
  useLegible();
    const userTz = useTimezone();
    const { confirm } = useConfirm(); // Initialize the hook here
    const { showToast } = useToast();
  
    // --- STATE ---
    const [viewMode, setViewMode] = useState<"list" | "detail">("list");
    const [detailTab, setDetailTab] = useState<"info" | "dashboard">("info");
  
  // Data
  const [yourGuilds, setYourGuilds] = useState<Guild[]>([]);
  const [directoryGuilds, setDirectoryGuilds] = useState<Guild[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [fieldsByGame, setFieldsByGame] = useState<Record<string, OptionGroup[]>>({});
  
  // Selection
  const [selectedGuildId, setSelectedGuildId] = useState<string | null>(null);
  const activeGuild = useMemo(() => 
    yourGuilds.find(g => g.id === selectedGuildId) || directoryGuilds.find(g => g.id === selectedGuildId), 
  [selectedGuildId, yourGuilds, directoryGuilds]);

  // Player grouping for roster display
  // Guilds that play two games (e.g. Anniversary + WoW Forever) show one game's
  // roster at a time; alts are grouped within that game only.
  const [rosterGameId, setRosterGameId] = useState<string>("");
  const rosterGameIds = useMemo(() => {
    const ids = new Set<string>();
    (activeGuild?.roster || []).forEach((m) => { if (m.gameId) ids.add(m.gameId); });
    return Array.from(ids);
  }, [activeGuild?.roster]);
  useEffect(() => { setRosterGameId(""); }, [activeGuild?.id]);
  const shownRosterGameId = rosterGameIds.length > 1
    ? (rosterGameIds.includes(rosterGameId) ? rosterGameId : (activeGuild?.gameId && rosterGameIds.includes(activeGuild.gameId) ? activeGuild.gameId : rosterGameIds[0]))
    : "";

  const playerGroups = useMemo(() => {
    if (!activeGuild?.roster) return [];
    
    const grouped = new Map<string, RosterMember[]>();
    activeGuild.roster
      .filter((m) => !shownRosterGameId || m.gameId === shownRosterGameId)
      .forEach(m => {
      const key = m.userId || m.characterId;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(m);
    });
    
    const groups: { userId: string; displayName: string; isOfficer: boolean; mainChar: RosterMember; altChars: RosterMember[]; allChars: RosterMember[] }[] = [];
    grouped.forEach((chars, key) => {
      chars.sort((a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime());
      groups.push({
        userId: key,
        displayName: chars[0].userDisplayName || chars[0].name,
        isOfficer: chars.some(c => c.isOfficer),
        mainChar: chars[0],
        altChars: chars.slice(1),
        allChars: chars,
      });
    });
    
    // Officers first, then by earliest joinedAt
    groups.sort((a, b) => {
      if (a.isOfficer !== b.isOfficer) return a.isOfficer ? -1 : 1;
      return new Date(a.mainChar.joinedAt).getTime() - new Date(b.mainChar.joinedAt).getTime();
    });
    
    return groups;
  }, [activeGuild?.roster, fieldsByGame, activeGuild?.gameId, shownRosterGameId]);

  // Auth
  const [isAuthed, setIsAuthed] = useState(false);

  // Forms
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  
// UI Feedback
const [inviteCopied, setInviteCopied] = useState(false);
const [codeCopied, setCodeCopied] = useState(false);
const [regeneratingInvite, setRegeneratingInvite] = useState(false);
const [botUrlCopied, setBotUrlCopied] = useState(false);

// Member Management
const [memberActionLoading, setMemberActionLoading] = useState<string | null>(null);
const [discordBotUrl, setDiscordBotUrl] = useState<string | null>(null);
const [discordStatus, setDiscordStatus] = useState<{configured: boolean; botEnabled?: boolean; openSeatsBoard?: boolean} | null>(null);

// Roster: Active character per player (for inline alt switching)
const [activeCharMap, setActiveCharMap] = useState<Record<string, number>>({});

// Roster: Expanded cards
const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

// Roster: Search
const [rosterSearch, setRosterSearch] = useState("");

// Roster: Pagination
const [rosterPage, setRosterPage] = useState(0);
const ROSTER_PAGE_SIZE = 20;

// Roster: Current week confirmation + availability maps
const [currentWeekConfirmMap, setCurrentWeekConfirmMap] = useState<Record<string, boolean>>({});
const [currentWeekAvailMap, setCurrentWeekAvailMap] = useState<Record<string, string>>({});
  
  // Join State
  const [joinCode, setJoinCode] = useState("");
  const [joinCharId, setJoinCharId] = useState("");

  // Request-to-join shortcut modal, launched from the directory cards.
  const [requestModalGuild, setRequestModalGuild] = useState<Guild | null>(null);
  const openRequestModal = (g: Guild) => {
    setRequestModalGuild(g);
    // Make sure the game's fields are loaded so the panel can verify server/faction.
    if (g.gameId && !fieldsByGame[g.gameId]) {
      fetch(`/games/${g.gameId}/fields`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setFieldsByGame((prev) => ({ ...prev, [g.gameId]: data })))
        .catch(() => {});
    }
  };

  // --- DIRECTORY ---
  // /guilds/directory carries no relationship flags; /guilds (the viewer's own
  // guilds) does. Merge so a card knows Owner / Member / Manage.
  const directoryView = useMemo(() => {
    const mine = new Map(yourGuilds.map(g => [g.id, g]));
    const base = directoryGuilds.length ? directoryGuilds : yourGuilds;
    return base.map(g => {
      const y = mine.get(g.id);
      return y ? { ...g, isOwner: y.isOwner, isOfficer: y.isOfficer } : g;
    });
  }, [yourGuilds, directoryGuilds]);
  const yourGuildIds = useMemo(() => new Set(yourGuilds.map(g => g.id)), [yourGuilds]);


  // --- ROSTER SEARCH ---
  const filteredPlayerGroups = useMemo(() => {
    if (!rosterSearch.trim()) return playerGroups;
    const q = rosterSearch.toLowerCase();
    return playerGroups.filter(p =>
      p.allChars.some(c => c.name.toLowerCase().includes(q)) ||
      p.displayName.toLowerCase().includes(q) ||
      p.allChars.some(c => (c.metadata?.class_name || '').toLowerCase().includes(q)) ||
      p.allChars.some(c => (c.metadata?.spec || c.metadata?.primary_spec_label || '').toLowerCase().includes(q))
    );
  }, [playerGroups, rosterSearch]);

  // Paginated roster groups
  const paginatedGroups = useMemo(() => {
    const start = rosterPage * ROSTER_PAGE_SIZE;
    return filteredPlayerGroups.slice(start, start + ROSTER_PAGE_SIZE);
  }, [filteredPlayerGroups, rosterPage, ROSTER_PAGE_SIZE]);
  const totalRosterPages = Math.ceil(filteredPlayerGroups.length / ROSTER_PAGE_SIZE);

  // Reset pagination when search changes
  useEffect(() => { setRosterPage(0); }, [rosterSearch]);

  // Rallify Rank: Based on total raid assignments across all characters a player owns
  const rankData = useMemo(() => {
    if (!playerGroups.length) return new Map<string, { tier: string; color: string; glowColor: string; count: number; rank: number }>();
    const counts = playerGroups.map(p => ({ userId: p.userId, count: p.mainChar.totalRaidCount || 0 }));
    const sorted = [...counts].sort((a, b) => b.count - a.count);
    const total = sorted.length;
    const result = new Map<string, { tier: string; color: string; glowColor: string; count: number; rank: number }>();
    sorted.forEach((item, index) => {
      const percentile = total > 1 ? (index / (total - 1)) * 100 : 0;
      let tier: string, color: string, glowColor: string;
      if (item.count === 0) { tier = 'Unranked'; color = '#555'; glowColor = 'transparent'; }
      else if (percentile <= 1 || (total > 1 && index === 0 && item.count > 0)) { tier = 'Platinum'; color = '#E5E4E2'; glowColor = 'rgba(229,228,226,0.3)'; }
      else if (percentile <= 10) { tier = 'Gold'; color = '#FFD700'; glowColor = 'rgba(255,215,0,0.25)'; }
      else if (percentile <= 25) { tier = 'Silver'; color = '#C0C0C0'; glowColor = 'rgba(192,192,192,0.2)'; }
      else { tier = 'Bronze'; color = '#CD7F32'; glowColor = 'rgba(205,127,50,0.2)'; }
      result.set(item.userId, { tier, color, glowColor, count: item.count, rank: index + 1 });
    });
    return result;
  }, [playerGroups]);

  // --- DATA LOADING ---

  // Launch waitlist: invite/PUG entrants ('member' tier) can't create guilds
  // while waitlist mode is on. The backend hard-blocks; this hides the button.
  const [guildCreationLocked, setGuildCreationLocked] = useState(false);
  useEffect(() => {
    fetch("/waitlist/status", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setGuildCreationLocked(d.waitlistMode === true && d.tier === "member");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/guilds", { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const fullData = await Promise.all(data.map(async (g: Guild) => {
            const dRes = await fetch(`/guilds/${g.id}`);
            return dRes.ok ? { ...g, ...(await dRes.json()) } : g;
          }));
          setYourGuilds(fullData);
          setIsAuthed(true);
        }
      } catch (e) { setIsAuthed(false); }

      try {
        const res = await fetch("/guilds/directory");
        if (res.ok) {
            setDirectoryGuilds(await res.json());
        }
      } catch {}

      try {
        const gRes = await fetch("/games");
        if (gRes.ok) setGames(await gRes.json());
        
        const cRes = await fetch("/characters", { credentials: "include" });
        if (cRes.ok) setCharacters(await cRes.json());
      } catch {}
    }
    load();
  }, []);

// Auto-open guild detail if openGuildId is in sessionStorage
useEffect(() => {
    const openGuildId = sessionStorage.getItem('openGuildId');
    if (!openGuildId) return;
    
    // Wait until guilds are actually loaded
    if (yourGuilds.length === 0 && directoryGuilds.length === 0) return;
    
    const allGuilds = [...yourGuilds, ...directoryGuilds];
    const targetGuild = allGuilds.find(g => g.id === openGuildId);
    
    if (targetGuild) {
      setSelectedGuildId(openGuildId);
      setViewMode("detail");

      // Honour deep-link from the home-page Command Center: optionally jump
      // straight to the Dashboard tab.
      const requestedDetailTab = sessionStorage.getItem('openGuildDetailTab');
      if (requestedDetailTab === 'dashboard' || requestedDetailTab === 'info') {
        setDetailTab(requestedDetailTab);
        sessionStorage.removeItem('openGuildDetailTab');
      }

      // Honour deep-link from the home-page Command Center: optionally pop
      // the existing Settings modal for owners. Re-uses openEditModal so all
      // form-population logic stays in one place.
      if (sessionStorage.getItem('openGuildSettingsModal') === '1' && targetGuild.isOwner) {
        sessionStorage.removeItem('openGuildSettingsModal');
        openEditModal(targetGuild);
      }

      sessionStorage.removeItem('openGuildId');
    } else {
      // Guild not found in either list, clear the storage to prevent infinite loop
      sessionStorage.removeItem('openGuildId');
    }
  }, [yourGuilds, directoryGuilds]);

  // Fetch game fields when viewing guild detail (for role colors)
  useEffect(() => {
    if (!activeGuild?.gameId) return;
    if (fieldsByGame[activeGuild.gameId]) return;
    
    fetch(`/games/${activeGuild.gameId}/fields`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setFieldsByGame(prev => ({ ...prev, [activeGuild.gameId]: data })))
      .catch(() => {});
  }, [activeGuild?.gameId]);

// Fetch full guild details when selecting a directory guild
useEffect(() => {
    if (!selectedGuildId) return;
    
    // Check if it's already in yourGuilds (which has full data)
    const inYourGuilds = yourGuilds.find(g => g.id === selectedGuildId);
    if (inYourGuilds) return;
    
    // It's a directory guild - fetch full details
    const dirGuild = directoryGuilds.find(g => g.id === selectedGuildId);
    if (!dirGuild) return;
    
    fetch(`/guilds/${selectedGuildId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setDirectoryGuilds(prev => prev.map(g => 
            g.id === selectedGuildId ? { ...g, ...data } : g
          ));
        }
      })
      .catch(() => {});
  }, [selectedGuildId, yourGuilds, directoryGuilds]);

  // --- ACTIONS ---

  // Replace a leaked invite code. The old link and code stop working at once.
  const handleRegenerateInvite = async () => {
    if (!activeGuild || regeneratingInvite) return;
    const ok = await confirm({
      title: "Make a new invite code?",
      description:
        "The current invite link and code stop working right away. Anyone who has the old one will need the new one. Members already in the guild are not affected.",
      confirmText: "Make new code",
      variant: "danger",
    });
    if (!ok) return;

    setRegeneratingInvite(true);
    try {
      const res = await fetch(`/guilds/${activeGuild.id}/regenerate-invite`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.inviteCode) {
        showToast(res.status === 403 ? "Only the guild leader and officers can do that." : "Could not make a new code. Try again.", "error");
        return;
      }
      setYourGuilds(prev => prev.map(g => (g.id === activeGuild.id ? { ...g, inviteCode: data.inviteCode } : g)));
      showToast("New invite code ready. The old one no longer works.", "success");
    } catch {
      showToast("Could not make a new code. Check your connection.", "error");
    } finally {
      setRegeneratingInvite(false);
    }
  };

  // --- MEMBER MANAGEMENT ---

  const handleToggleOfficer = async (characterId: string, currentlyOfficer: boolean) => {
    if (!activeGuild) return;

    // 1. Confirm Demotion (skip confirm for Promotion)
    if (currentlyOfficer) {
      const isConfirmed = await confirm({
        title: "Demote Officer?",
        description: "Are you sure you want to remove Officer status? They will lose access to officer settings.",
        confirmText: "Demote",
        variant: "danger",
      });
      if (!isConfirmed) return;
    }

    setMemberActionLoading(characterId);
    
    try {
      const res = await fetch(`/api/guilds/${activeGuild.id}/members/${characterId}/officer`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isOfficer: !currentlyOfficer })
      });
      
      if (res.ok) {
        // Update local state
        setYourGuilds(prev => prev.map(g => {
          if (g.id !== activeGuild.id) return g;
          return {
            ...g,
            roster: g.roster?.map(m => 
              m.characterId === characterId 
                ? { ...m, isOfficer: !currentlyOfficer, guildRank: !currentlyOfficer ? 'Officer' : 'Member' }
                : m
            )
          };
        }));
      } else {
        const err = await res.json();
        // Pretty Error Modal
        await confirm({
          title: "Update Failed",
          description: err.error || 'Failed to update officer status',
          confirmText: "Okay",
          variant: "danger",
        });
      }
    } catch (e) {
      // Pretty Network Error
      await confirm({
        title: "Network Error",
        description: "Could not reach the server. Please try again.",
        confirmText: "Okay",
        variant: "danger",
      });
    } finally {
      setMemberActionLoading(null);
    }
  };

  const handleRemoveMember = async (characterId: string, memberName: string) => {
    if (!activeGuild) return;

    // 1. Pretty Confirmation Modal
    const isConfirmed = await confirm({
        title: `Remove ${memberName}?`,
        description: `Are you sure you want to remove ${memberName} from ${activeGuild.name}? This will also remove them from any raid assignments.`,
        confirmText: "Remove Member",
        variant: "danger",
    });

    if (!isConfirmed) return;
    
    setMemberActionLoading(characterId);
    
    try {
      const res = await fetch(`/api/guilds/${activeGuild.id}/members/${characterId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        // Update local state
        setYourGuilds(prev => prev.map(g => {
          if (g.id !== activeGuild.id) return g;
          return {
            ...g,
            roster: g.roster?.filter(m => m.characterId !== characterId),
            memberCount: (g.memberCount || g.roster?.length || 1) - 1
          };
        }));
      } else {
        const err = await res.json();
        // Pretty Error Modal
        await confirm({
            title: "Could not remove member",
            description: err.error || 'Failed to remove member',
            confirmText: "Okay",
            variant: "danger",
        });
      }
    } catch (e) {
      // Pretty Network Error
      await confirm({
        title: "Network Error",
        description: "Could not reach the server. Please try again.",
        confirmText: "Okay",
        variant: "danger",
      });
    } finally {
      setMemberActionLoading(null);
    }
  };

  const fetchDiscordBotUrl = async () => {
    if (!activeGuild) return;
    try {
      const res = await fetch(`/api/guilds/${activeGuild.id}/discord-bot-url`, { credentials: 'include' });
      if (res.ok) {
        const { inviteUrl } = await res.json();
        setDiscordBotUrl(inviteUrl);
      }
    } catch {}
  };

  const fetchDiscordStatus = async () => {
    if (!activeGuild) return;
    try {
      const res = await fetch(`/api/guilds/${activeGuild.id}/discord-status`, { credentials: 'include' });
      if (res.ok) {
        setDiscordStatus(await res.json());
      }
    } catch {}
  };

  // Fetch discord info when viewing owned or officer guild
  useEffect(() => {
    if (activeGuild?.isOwner || activeGuild?.isOfficer) {
      fetchDiscordBotUrl();
      fetchDiscordStatus();
    }
  }, [activeGuild?.id, activeGuild?.isOwner, activeGuild?.isOfficer]);

  // Reset state when changing guilds
  useEffect(() => { setActiveCharMap({}); setRosterSearch(""); setExpandedCards(new Set()); }, [selectedGuildId]);

  // The setup tour asks for a pane; its last step wants "Recruit from Discord",
  // which lives inside the dashboard's Requests sub-tab. sessionStorage covers
  // the dashboard mounting fresh, the event covers it already being open.
  const handleTourStage = useCallback((s: GuildStage, subtab = "requests") => {
    setDetailTab(s);
    if (s === "dashboard") {
      try { sessionStorage.setItem("openDashboardSubtab", subtab); } catch {}
      window.dispatchEvent(new CustomEvent("lfm:dashboard-subtab", { detail: { tab: subtab } }));
    }
  }, []);

  // Fetch current week confirmation data for dual-week display
  useEffect(() => {
    if (!selectedGuildId || viewMode !== 'detail') return;
    // "current" is resolved by the server with the guild's own reset rule.
    fetch(`/guilds/${selectedGuildId}?weekStart=current`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.roster) {
          const confirmMap: Record<string, boolean> = {};
          const availMap: Record<string, string> = {};
          data.roster.forEach((m: any) => {
            if (m.userId) {
              confirmMap[m.userId] = !!m.isConfirmed;
              if (m.userAvailability) availMap[m.userId] = m.userAvailability;
            }
          });
          setCurrentWeekConfirmMap(confirmMap);
          setCurrentWeekAvailMap(availMap);
        }
      })
      .catch(() => {});
  }, [selectedGuildId, viewMode]);

  // The guild itself saves fine even if the logo doesn't, so a failure here is
  // reported as a toast rather than blocking the save. Upload rate limits send
  // player-facing copy in `message`.
  const handleLogoUpload = async (guildId: string, file: File) => {
    const fd = new FormData();
    fd.append("logo", file);
    const res = await fetch(`/guilds/${guildId}/logo`, { method: "POST", credentials: "include", body: fd });
    if (res.ok) {
      const { logoUrl } = await res.json();
      return logoUrl;
    }
    const err = await res.json().catch(() => ({} as any));
    showToast(
      err.message || "Guild saved, but the logo upload failed. Try again from Edit.",
      "error"
    );
    return null;
  };

  const handleFieldsLoaded = useCallback((gameId: string, data: OptionGroup[]) => {
    setFieldsByGame((prev) => (prev[gameId] ? prev : { ...prev, [gameId]: data }));
  }, []);

  const handleDelete = async (): Promise<string | null> => {
    if (!activeGuild) return "Guild not found.";

    const isConfirmed = await confirm({
      title: "Disband Guild?",
      description: `Are you sure you want to disband ${activeGuild.name}? This action cannot be undone.`,
      confirmText: "Disband Guild",
      variant: "danger",
    });

    if (!isConfirmed) return null;

    try {
      const res = await fetch(`/guilds/${activeGuild.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.ok || res.status === 204) {
        setYourGuilds((prev) => prev.filter((g) => g.id !== activeGuild.id));
        setDirectoryGuilds((prev) => prev.filter((g) => g.id !== activeGuild.id));
        setIsModalOpen(false);
        setViewMode("list");
        setSelectedGuildId(null);
        return null;
      }
      const err = await res.json();
      return err.error || "Failed to disband guild.";
    } catch (e) {
      return "Network error during disband.";
    }
  };

  const handleCreateOrUpdate = async (
    values: GuildFormDraft & { logoFile: File | null }
  ): Promise<string | null> => {
    if (!values.gameId) return "Game is required.";
    if (!values.name.trim()) return "Guild Name is required.";
    if (!values.tag.trim()) return "Guild Tag is required.";

    const body = {
      gameId: values.gameId,
      name: values.name.trim(),
      tag: values.tag.trim(),
      region: values.region.trim() || null,
      serverName: values.serverName.trim() || null,
      faction: formatFaction(values.faction),
      aboutRaw: values.aboutRaw.trim() || null,
      contactInfo: values.contactInfo.trim() || null,
      scheduleSummary: values.scheduleSummary.trim() || null,
      metadata: { recruiting: values.recruiting },
    };

    try {
      let res;

      if (modalMode === "create") {
        res = await fetch("/guilds", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });
      } else if (activeGuild) {
        res = await fetch(`/guilds/${activeGuild.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });
      }

      if (res && res.ok) {
        const resBody = await res.json().catch(() => ({}));
        const finalId = modalMode === "create" ? resBody?.id : activeGuild?.id;

        let uploadedLogoUrl: string | null = null;
        if (values.logoFile && finalId) {
          uploadedLogoUrl = await handleLogoUpload(finalId, values.logoFile);
        }

        if (modalMode === "create" && resBody?.id) {
          let detail: Partial<Guild> = {};
          try {
            const dRes = await fetch(`/guilds/${resBody.id}`, { credentials: "include" });
            if (dRes.ok) detail = await dRes.json();
          } catch {}

          const newGuild: Guild = {
            ...resBody,
            ...detail,
            isOwner: true,
            memberCount: 1,
            recruiting: values.recruiting,
          };
          if (uploadedLogoUrl) newGuild.logoUrl = uploadedLogoUrl;
          setYourGuilds((prev) => [...prev, newGuild]);
          setDirectoryGuilds((prev) => [...prev, newGuild]);
          setSelectedGuildId(resBody.id);
          setDetailTab("info");
          setViewMode("detail");
          requestGuildSetupTour(resBody.id, newGuild.name);
        } else if (modalMode === "edit" && activeGuild) {
          const gid = activeGuild.id;
          const patch: Partial<Guild> = {
            name: body.name,
            tag: body.tag,
            region: body.region,
            serverName: body.serverName,
            faction: body.faction,
            aboutRaw: body.aboutRaw,
            contactInfo: body.contactInfo,
            scheduleSummary: body.scheduleSummary,
            recruiting: values.recruiting,
            metadata: {
              ...(activeGuild.metadata || {}),
              recruiting: values.recruiting,
              region: body.region,
            },
          };
          if (uploadedLogoUrl) patch.logoUrl = uploadedLogoUrl;
          setYourGuilds((prev) => prev.map((g) => (g.id === gid ? { ...g, ...patch } : g)));
          setDirectoryGuilds((prev) => prev.map((g) => (g.id === gid ? { ...g, ...patch } : g)));
        }

        setIsModalOpen(false);
        return null;
      }
      const err = await res?.json();
      return err?.error || "Server rejected request. Check constraints.";
    } catch (error) {
      return "Network error. Please check your connection.";
    }
  };

  const handleJoin = async () => {
    if (!joinCode || !joinCharId) return;
    try {
      const res = await fetch("/guilds/join", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ inviteCode: joinCode, characterId: joinCharId }),
      });
      const data = await res.json();
      if (res.ok) {
        window.location.reload();
      } else {
        const msg =
          data.error === "invalid_invite"
            ? "Invalid invite code"
            : data.error === "invalid_character"
              ? "Invalid character selected"
              : "Failed to join guild";
        showToast(msg, "error");
      }
    } catch (err) {
      showToast("Network error. Please try again.", "error");
    }
  };

  const openCreateModal = () => {
    setModalMode("create");
    setIsModalOpen(true);
  };

  const openEditModal = (g: Guild) => {
    setModalMode("edit");
    setSelectedGuildId(g.id);
    setIsModalOpen(true);
  };

  // --- SUB-COMPONENTS ---


// --- HELPER: Parse Preferences ---
function getCharacterRaidInfo(member: RosterMember, fields: OptionGroup[] | null | undefined) {
  if (!fields || !member.options) return { isRL: false, isML: false, interests: [] };

  const prefGroup = fields.find(g => g.key === "raid_preferences");
  const interestGroup = fields.find(g => g.key === "raid_interest");
  
  let isRL = false;
  let isML = false;
  const interests: string[] = [];

  member.options.forEach((opt: any) => {
    // Preferences
    if (prefGroup && Number(opt.group_id) === Number(prefGroup.id)) {
      const o = prefGroup.options.find(x => Number(x.id) === Number(opt.option_id));
      if (o?.value_key === "willing_raid_lead" || o?.key === "willing_raid_lead") isRL = true;
      if (o?.value_key === "willing_loot_master" || o?.key === "willing_loot_master") isML = true;
    }
    // Interests
    if (interestGroup && Number(opt.group_id) === Number(interestGroup.id)) {
      const o = interestGroup.options.find(x => Number(x.id) === Number(opt.option_id));
      if (o) interests.push(o.label);
    }
  });

  return { isRL, isML, interests };
}

// (DualWeekAvailGrid, RallifyRankBadge and availability helpers are defined at
// module scope above — keeping them out of GuildsPage prevents React from
// remounting them on every parent render.)

// --- HELPER: Toggle expand ---
const toggleExpand = (userId: string) => {
  setExpandedCards(prev => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    return next;
  });
};

// --- RENDER ---

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-24 relative">
      
      {/* 1. HEADER (detail view; the directory renders its own) */}
      {viewMode === "detail" && (
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
            {viewMode === "detail" && activeGuild ? (
                // Breadcrumb for Detail View
                <div className="flex flex-col gap-1">
                    <button 
                        onClick={() => { setViewMode("list"); setSelectedGuildId(null); }}
                        className="text-[13px] font-semibold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors flex items-center gap-1"
                    >
                        &larr; All guilds
                    </button>
                    {/* The guild's name lives in the profile hero below (GuildProfile);
                        this strip is just the way back plus the officer controls. */}
                </div>
            ) : (
                // List View Header
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Guilds</div>
                    <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>Guild directory</h1>
                    <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
                </div>
            )}
        </div>

        {/* View Mode Actions */}
        <div className="flex items-center gap-3">
            
            {/* Detail Actions */}
            {viewMode === "detail" && activeGuild && (activeGuild.isOwner || activeGuild.isOfficer) && (
                <div className="flex items-center gap-2">
                    {/* Dashboard / Info toggle. Every control on this row is
                        pinned to h-8 so the header reads as one strip instead
                        of four boxes at four slightly different heights. */}
                    <div data-tour="guild-panes" className="flex items-center h-8 bg-[var(--c-surface)] rounded-lg border border-[var(--c-line)] p-0.5">
                        <button
                            onClick={() => setDetailTab("info")}
                            className={`flex items-center h-full px-3 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                                detailTab === "info"
                                    ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)] shadow"
                                    : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
                            }`}
                        >
                            Guild Info
                        </button>
                        <button
                            onClick={() => setDetailTab("dashboard")}
                            className={`flex items-center h-full px-3 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                                detailTab === "dashboard"
                                    ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)] shadow"
                                    : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
                            }`}
                        >
                            <span className="flex items-center gap-1.5">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                                Dashboard
                            </span>
                        </button>
                    </div>
                    {/* Replay the setup walkthrough. The tour is one-time by
                        design, so without this the only way back in is the
                        console. */}
                    <button
                        onClick={() => startGuildSetupTour(activeGuild.id, activeGuild.name)}
                        title="Replay the guild setup guide"
                        aria-label="Replay the guild setup guide"
                        className="flex items-center justify-center w-8 h-8 shrink-0 border border-amber-400/25 bg-amber-500/10 text-amber-700/70 dark:text-amber-300/70 rounded-lg hover:bg-amber-500/20 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                    </button>
                    {activeGuild.isOwner && (
                        <button
                            onClick={() => openEditModal(activeGuild)}
                            className="flex items-center h-8 px-3 border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink)] text-xs font-bold rounded-lg hover:bg-[var(--c-sunken-2)] transition-colors"
                        >
                            <span>Settings</span>
                        </button>
                    )}
                    {/* Boxed like the pane toggle: bare in the header it read as
                        four loose bits floating after the buttons. */}
                    {(activeGuild.isOwner || activeGuild.isOfficer) && (
                      <GuildWeeklyRelease
                        guildId={activeGuild.id}
                        context="guild"
                        className="h-8 pl-2.5 pr-1.5 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)]"
                      />
                    )}
                </div>
            )}
        </div>
      </div>
      )}

      {/* 2. MAIN CONTENT AREA */}
      {viewMode === "list" ? (
        <GuildDirectory
            guilds={directoryView}
            yourGuildIds={yourGuildIds}
            games={games}
            isAuthed={isAuthed}
            characters={characters}
            creationLocked={guildCreationLocked}
            onOpen={(g) => { setSelectedGuildId(g.id); setViewMode("detail"); setDetailTab("info"); }}
            onManage={(g) => { setSelectedGuildId(g.id); setViewMode("detail"); setDetailTab("info"); }}
            onRequest={(g) => { const full = directoryView.find(x => x.id === g.id); if (full) openRequestModal(full); }}
            onCreate={openCreateModal}
            joinPanel={
                isAuthed ? (
                    <div className="p-4 bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/20 rounded-3xl flex flex-wrap items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 ml-1">
                        <svg className="w-5 h-5 text-emerald-700 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>
                        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Join a guild?</span>
                        <div className="relative group">
                            <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-700/70 dark:text-emerald-400/70 text-[10px] font-bold flex items-center justify-center cursor-help hover:bg-emerald-500/30 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors">?</span>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-xl">
                                Guild leaders can generate invite codes from their guild settings
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                            </div>
                        </div>
                    </div>
                    <input 
                            value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="Enter invite code..."
                            className="bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-[var(--c-ink)] placeholder-white/30 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-40 transition-colors"
                        />
                        <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-[var(--c-ink-4)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                        <select 
                            value={joinCharId} onChange={e => setJoinCharId(e.target.value)}
                            className="bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-52 transition-colors"
                        >
                            <option value="">Select Char...</option>
                            {characters.map(c => (
                                <option 
                                    key={c.id} 
                                    value={c.id}
                                    disabled={!!c.guildId}
                                    className={c.guildId ? "text-[var(--c-ink-4)] italic" : ""}
                                >
                                    {c.name}{c.guildId ? ` (in guild)` : ""}
                                </option>
                            ))}
                        </select>
                        </div>
                        <button onClick={handleJoin} disabled={!joinCode || !joinCharId} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-lg shadow-emerald-900/30">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                            Join
                        </button>
                    </div>
                ) : null
            }
        />
      ) : (
        /* DETAIL VIEW */
        activeGuild ? (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {/* DASHBOARD TAB (officers/owners only) */}
          {detailTab === "dashboard" && (activeGuild.isOwner || activeGuild.isOfficer) ? (
              <GuildDashboard guildId={activeGuild.id} />
          ) : (
          <>
          {/* PUBLIC PROFILE: what a recruit or member sees (GuildProfile.tsx). */}
          <GuildProfile
            guild={activeGuild}
            joinPanel={isAuthed && activeGuild.recruiting === true ? (
              <RequestToJoinPanel
                guild={activeGuild}
                characters={characters}
                fields={fieldsByGame[activeGuild.gameId] || []}
                className="w-full"
              />
            ) : null}
          />

                {/* CONTENT */}
                <div className="mt-10 space-y-8">
                        {/* OFFICER TOOLS: everything that runs the guild, under one heading
                            below the public profile. Panels are unchanged (tour anchors). */}
                        {(activeGuild.isOwner || activeGuild.isOfficer) && (
                            <section className="space-y-4">
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Officers</div>
                                    <h2 className="mt-1 text-2xl text-[var(--c-ink)]" style={DISPLAY}>Manage guild</h2>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 min-w-0 w-full">
                                {/* Recruit Panel */}
                                {activeGuild.inviteCode && (
                                    <div data-tour="guild-invite" className="flex-1 p-4 rounded-xl bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] border border-emerald-500/15 space-y-3">
                                        <h3 className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">Recruit</h3>
                                        <div className="flex gap-2">
                                            <code className="flex-1 bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-2 py-1.5 text-[10px] text-[var(--c-ink-2)] font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                                                {`${window.location.origin}/#Invite?code=${activeGuild.inviteCode}`}
                                            </code>
                                            <button
                                                onClick={() => {
                                                    const url = `${window.location.origin}/#Invite?code=${activeGuild.inviteCode}`;
                                                    navigator.clipboard.writeText(url);
                                                    setInviteCopied(true);
                                                    setTimeout(() => setInviteCopied(false), 2000);
                                                }}
                                                className={`px-2 py-1 border rounded text-[10px] font-bold transition-all whitespace-nowrap ${
                                                    inviteCopied
                                                        ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-700 dark:text-emerald-300"
                                                        : "bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                                                }`}
                                            >
                                                {inviteCopied ? "✓" : "Copy"}
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2 pt-2 border-t border-[var(--c-line-soft)]">
                                            <span className="text-[9px] text-[var(--c-ink-4)] uppercase tracking-wider">Code</span>
                                            <code className="text-[11px] text-[var(--c-ink-2)] font-mono tracking-widest">{activeGuild.inviteCode}</code>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(activeGuild.inviteCode!);
                                                    setCodeCopied(true);
                                                    setTimeout(() => setCodeCopied(false), 2000);
                                                }}
                                                className={`px-1.5 py-0.5 border rounded text-[9px] font-bold transition-all ${
                                                    codeCopied
                                                        ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-700 dark:text-cyan-300"
                                                        : "bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)]"
                                                }`}
                                            >
                                                {codeCopied ? "✓" : "Copy"}
                                            </button>
                                            <button
                                                onClick={handleRegenerateInvite}
                                                disabled={regeneratingInvite}
                                                title="Leaked? Make a new code. The old link and code stop working."
                                                className="ml-auto px-1.5 py-0.5 border rounded text-[9px] font-bold transition-all bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-red-600 dark:hover:text-red-400 hover:border-red-500/40 disabled:opacity-50"
                                            >
                                                {regeneratingInvite ? "..." : "New code"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Discord Panel */}
                                {discordBotUrl && (
                                    <div data-tour="guild-discord" className="flex-1 p-4 rounded-xl bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] border border-[#5865F2]/20 space-y-3">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                                            </svg>
                                            <h3 className="text-[10px] font-bold text-[#5865F2] uppercase tracking-widest">Discord</h3>
                                        </div>
                                        {discordStatus?.configured && discordStatus?.botEnabled ? (
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                                    Connected
                                                </div>
                                                <p className="text-[10px] text-[var(--c-ink-4)]">
                                                    Use <code className="bg-[var(--c-surface)] px-1 rounded">/ralli check</code> to send availability checks.
                                                </p>
                                                {/* Open seats board: one live message the bot keeps up to date
                                                    in this channel, plus a "View open raids" button on its posts. */}
                                                <label className="flex cursor-pointer items-center gap-2 pt-1 text-[11px] font-semibold text-[var(--c-ink-2)]">
                                                    <input
                                                        type="checkbox"
                                                        className="h-3.5 w-3.5 accent-[#5865F2]"
                                                        checked={discordStatus.openSeatsBoard !== false}
                                                        onChange={async (e) => {
                                                            const enabled = e.target.checked;
                                                            setDiscordStatus(prev => prev ? { ...prev, openSeatsBoard: enabled } : prev);
                                                            const res = await fetch(`/api/guilds/${activeGuild.id}/discord-open-seats`, {
                                                                method: 'PUT', credentials: 'include',
                                                                headers: { 'Content-Type': 'application/json' },
                                                                body: JSON.stringify({ enabled }),
                                                            }).catch(() => null);
                                                            if (!res || !res.ok) setDiscordStatus(prev => prev ? { ...prev, openSeatsBoard: !enabled } : prev);
                                                        }}
                                                    />
                                                    Post open seats in this channel
                                                </label>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="flex gap-2">
                                                    <a href={discordBotUrl} target="_blank" rel="noopener noreferrer"
                                                        className="flex-1 px-3 py-1.5 bg-[#5865F2] hover:bg-[#4752C4] text-white text-[10px] font-bold rounded-lg text-center transition-colors">
                                                        Add to Discord
                                                    </a>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(discordBotUrl);
                                                            setBotUrlCopied(true);
                                                            setTimeout(() => setBotUrlCopied(false), 2000);
                                                        }}
                                                        className={`px-2 py-1.5 border rounded-lg text-[10px] font-bold transition-all ${
                                                            botUrlCopied
                                                                ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-700 dark:text-emerald-300"
                                                                : "bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                                                        }`}
                                                    >
                                                        {botUrlCopied ? "✓" : "Copy"}
                                                    </button>
                                                </div>
                                                <p className="text-[9px] text-[var(--c-ink-4)]">
                                                    Then run <code className="bg-[var(--c-surface)] px-0.5 rounded">/ralli setup</code> in your server.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                </div>
                                <GuildReadyBar
                                    guildId={activeGuild.id}
                                    onOpenDashboard={(tab) => handleTourStage("dashboard", tab)}
                                />
                            </section>
                        )}

                        {/* Games this guild plays (officers manage them here). */}
                        {isAuthed && (
                            <GuildGamesPanel guildId={activeGuild.id} />
                        )}

                        {/* Who the guild is recruiting (officers). Feeds the directory card. */}
                        {isAuthed && (activeGuild.isOwner || activeGuild.isOfficer) && (
                            <GuildRecruitingPanel guildId={activeGuild.id} recruiting={activeGuild.recruiting === true} />
                        )}


                        <section>
                            {/* Roster Header with Search — NO random dates (#7) */}
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-baseline gap-3">
                                    <h3 className="text-base font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Active Roster</h3>
                                    <span className="text-sm text-[var(--c-ink-4)]">
                                        {filteredPlayerGroups.length} player{filteredPlayerGroups.length !== 1 ? 's' : ''}
                                    </span>
                                    {rosterGameIds.length > 1 && (
                                        <div className="ml-2 flex items-center gap-1 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] p-0.5">
                                            {rosterGameIds.map((gid) => {
                                                const sel = gid === shownRosterGameId;
                                                const name = games.find((g) => g.id === gid)?.name || "Game";
                                                return (
                                                    <button
                                                        key={gid}
                                                        onClick={() => { setRosterGameId(gid); setRosterPage(0); }}
                                                        className={`rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${sel ? "bg-[var(--c-surface)] text-[var(--c-accent-ink)] shadow-[var(--c-shadow-sm)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
                                                    >
                                                        {name.replace(/^World of Warcraft:?\s*/i, "") || name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {/* Roster Search (#2) */}
                                <div className="relative group">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--c-ink-4)] group-focus-within:text-[var(--c-accent-ink)] transition-colors pointer-events-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input type="text" value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)} placeholder="Search roster..."
                                        className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg pl-8 pr-3 py-2 text-sm text-[var(--c-ink)] focus:border-cyan-500/30 focus:bg-[var(--c-surface)] outline-none w-52 transition-all placeholder:text-[var(--c-ink-4)]" />
                                </div>
                            </div>

                            {/* Roster Grid — 2 columns with expand/collapse */}
                            {filteredPlayerGroups.length > 0 ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                                    {paginatedGroups.map(player => {
                                        const activeIdx = activeCharMap[player.userId] || 0;
                                        const activeChar = player.allChars[activeIdx] || player.mainChar;
                                        const fields = fieldsByGame[activeGuild.gameId] || [];
                                        const charClass = activeChar.metadata?.class_name || '';
                                        const charSpec = activeChar.metadata?.primary_spec_label || activeChar.metadata?.spec || '';
                                        const classColor = getClassColor(charClass, fields) || '#888';
                                        const roleColor = getRoleColor(activeChar.metadata?.primary_role || '', fields) || '#666';
                                        const renderUrl = activeChar.metadata?.render_url;
                                        const wclScore = activeChar.metadata?.wcl_perf?.score;
                                        const wclUrl = activeChar.metadata?.wcl_perf?.url;
                                        const bgImage = getClassArtUrl(charClass);
                                        const { isRL, isML } = getCharacterRaidInfo(activeChar, fields);
                                        const rank = rankData.get(player.userId);
                                        const canSeeConfirm = activeGuild.isOwner || activeGuild.isOfficer;
                                        const currentWeekConfirmed = currentWeekConfirmMap[player.userId] || false;
                                        const nextWeekConfirmed = activeChar.isConfirmed || false;
                                        const currentAvail = currentWeekAvailMap[player.userId];
                                        const nextAvail = activeChar.userAvailability;
                                        const isExpanded = expandedCards.has(player.userId);

                                        const allInterests = new Set<string>();
                                        player.allChars.forEach(c => {
                                          const { interests: ci } = getCharacterRaidInfo(c, fields);
                                          ci.forEach(i => allInterests.add(i));
                                        });

                                        const formatDate = (dateStr: string) => {
                                          if (!dateStr) return "Unknown";
                                          return new Intl.DateTimeFormat('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            timeZone: userTz
                                          }).format(new Date(dateStr));
                                        };

                                        return (
                                            <div key={player.userId}
                                                className="group/card relative rounded-xl overflow-hidden border border-[var(--c-line-soft)] hover:border-[var(--c-line-strong)] transition-all duration-300"
                                                style={{ borderLeftWidth: '3px', borderLeftColor: classColor }}>

                                                {/* CLASS_ART Background — matching RosterPage visibility (#2) */}
                                                <div className="absolute inset-0 z-0 bg-[var(--c-surface)]">
                                                    {bgImage ? (
                                                        <>
                                                            <img src={bgImage} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                className="w-full h-full object-cover opacity-40 group-hover/card:opacity-50 transition-opacity duration-500 scale-100 group-hover/card:scale-[1.03]" />
                                                            <div className="absolute inset-0 bg-gradient-to-r from-[var(--c-surface)] via-[var(--c-surface)] to-[var(--c-surface)]" />
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.04]" />
                                                    )}
                                                </div>

                                                {/* Card Content */}
                                                <div className="relative z-10">
                                                    {/* COLLAPSED ROW — always visible, click to expand (#6) */}
                                                    <button onClick={() => toggleExpand(player.userId)} className="w-full text-left p-4 flex gap-3 items-center">
                                                        {/* Character Render */}
                                                        <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-[var(--c-line)]"
                                                            style={{ background: `linear-gradient(135deg, ${classColor}25, ${classColor}08)` }}>
                                                            {renderUrl ? (
                                                                <img src={renderUrl} alt={activeChar.name} className="w-full h-full object-cover object-top" loading="lazy" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-xl font-black" style={{ color: tc(classColor) }}>
                                                                    {activeChar.name.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Name + Spec (#1 bigger fonts, #8 no role label, #9 styled officer badge) */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                                            <span className="text-lg font-bold truncate" style={{ color: tc(classColor) }}>{activeChar.name}</span>
                                                                {/* GM supersedes Officer rather than stacking with it: the
                                                                    owner is an officer by definition, so showing both reads as
                                                                    two ranks. Brighter amber than the Officer badge so the top
                                                                    rank is the one that catches the eye first. */}
                                                                {player.userId && player.userId === activeGuild.ownerUserId ? (
                                                                    <span
                                                                        title="Guild Master"
                                                                        className="px-1.5 py-0.5 rounded bg-amber-400/25 border border-amber-400/50 text-amber-800 dark:text-amber-200 text-[10px] font-bold uppercase tracking-wider"
                                                                    >
                                                                        GM
                                                                    </span>
                                                                ) : player.isOfficer ? (
                                                                    <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider">Officer</span>
                                                                ) : null}
                                                                {isRL && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/25 text-amber-700 dark:text-amber-400 text-[9px] font-bold uppercase">RL</span>}
                                                                {isML && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/25 text-emerald-700 dark:text-emerald-400 text-[9px] font-bold uppercase">ML</span>}
                                                                {player.altChars.length > 0 && (
                                                                    <span className="text-xs text-[var(--c-ink-4)]">+{player.altChars.length} alt{player.altChars.length > 1 ? 's' : ''}</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: roleColor }} />
                                                                <span className="text-sm text-[var(--c-ink-3)] truncate">
                                                                    {charSpec ? `${charClass} · ${charSpec}` : charClass || 'Unknown'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Right: WCL + Rank + Confirm + Chevron */}
                                                        <div className="flex items-center gap-4 flex-shrink-0">
                                                        {wclScore ? (
                                                                <div className="flex flex-col items-center w-12">
                                                                    <div className="h-9 flex items-center justify-center text-2xl font-black leading-none tabular-nums" style={{ color: tc(getParseColor(wclScore)) }}>{wclScore}</div>
                                                                    <div className="text-[8px] uppercase tracking-wider text-[var(--c-ink-4)] mt-0.5">parse</div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex flex-col items-center w-12 hidden sm:flex">
                                                                    <div className="h-9 flex items-center justify-center text-lg font-bold leading-none text-[var(--c-ink-4)]">—</div>
                                                                    <div className="text-[8px] uppercase tracking-wider text-[var(--c-ink-4)] mt-0.5">parse</div>
                                                                </div>
                                                            )}

                                                            {/* Rallify Rank */}
                                                            {rank && <RallifyRankBadge {...rank} />}

                                                            {/* Confirmation tooltips — officers only (#3 with z-[999]) */}
                                                            {canSeeConfirm && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <div className="relative group/cw">
                                                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border ${
                                                                            currentWeekConfirmed ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-700 dark:text-cyan-300' : 'bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]'
                                                                        }`}>{currentWeekConfirmed ? '✓' : '·'}</div>
                                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/cw:opacity-100 transition-opacity z-[999] shadow-2xl">
                                                                            Current Week: <span className={currentWeekConfirmed ? 'text-cyan-700 dark:text-cyan-400 font-bold' : 'text-[var(--c-ink-3)]'}>{currentWeekConfirmed ? 'Confirmed' : 'Unconfirmed'}</span>
                                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0a0a0f]"></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="relative group/nw">
                                                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border ${
                                                                            nextWeekConfirmed ? 'bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-300' : 'bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]'
                                                                        }`}>{nextWeekConfirmed ? '✓' : '·'}</div>
                                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/nw:opacity-100 transition-opacity z-[999] shadow-2xl">
                                                                            Next Week: <span className={nextWeekConfirmed ? 'text-amber-700 dark:text-amber-400 font-bold' : 'text-[var(--c-ink-3)]'}>{nextWeekConfirmed ? 'Confirmed' : 'Unconfirmed'}</span>
                                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0a0a0f]"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <svg className={`w-5 h-5 text-[var(--c-ink-4)] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                    </button>

                                                    {/* EXPANDED SECTION — mounted only when open so collapsed cards pay zero render cost */}
                                                    {isExpanded && (
                                                    <div className="px-4 pb-4 space-y-4 border-t border-[var(--c-line-soft)] animate-in fade-in slide-in-from-top-1 duration-150">
                                                            {/* Stats row */}
                                                            <div className="flex items-center gap-4 pt-3 text-xs text-[var(--c-ink-4)]">
                                                                <span>Joined {formatDate(activeChar.joinedAt)}</span>
                                                                {activeChar.ilvl && <><span>·</span><span className="font-mono text-[var(--c-ink-3)]">{activeChar.ilvl} ilvl</span></>}
                                                                {(activeChar.totalRaidCount != null && activeChar.totalRaidCount > 0) && <><span>·</span><span className="font-mono text-[var(--c-ink-3)]">{activeChar.totalRaidCount} raid{activeChar.totalRaidCount !== 1 ? 's' : ''}</span></>}
                                                            </div>

                                                            {/* Raid Interests */}
                                                            {allInterests.size > 0 && (
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {Array.from(allInterests).map(raid => (
                                                                        <span key={raid} className="px-2.5 py-1 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-xs text-[var(--c-ink-3)]">{raid}</span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Alt Characters — clickable switcher, changes card info (#6 inline) */}
                                                            {player.allChars.length > 1 && (
                                                                <div>
                                                                    <div className="text-[10px] uppercase font-bold text-[var(--c-ink-4)] tracking-widest mb-2">Characters</div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {player.allChars.map((alt, altIdx) => {
                                                                            const altClass = alt.metadata?.class_name || '';
                                                                            const altColor = getClassColor(altClass, fields) || '#888';
                                                                            const altRender = alt.metadata?.render_url;
                                                                            const altSpec = alt.metadata?.primary_spec_label || alt.metadata?.spec || '';
                                                                            const isActive = altIdx === activeIdx;
                                                                            const altWcl = alt.metadata?.wcl_perf?.score;
                                                                            return (
                                                                                <button key={alt.characterId}
                                                                                    onClick={(e) => { e.stopPropagation(); setActiveCharMap(prev => ({ ...prev, [player.userId]: altIdx })); }}
                                                                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-all ${
                                                                                        isActive ? 'border-[var(--c-line-strong)] bg-[var(--c-sunken)]' : 'border-[var(--c-line-soft)] hover:border-[var(--c-line)] bg-transparent hover:bg-[var(--c-bg-veil)]'
                                                                                    }`}
                                                                                    style={isActive ? { borderColor: `${altColor}50`, backgroundColor: `${altColor}10` } : {}}>
                                                                                    <div className="w-7 h-7 rounded overflow-hidden bg-[var(--c-surface)] flex-shrink-0">
                                                                                        {altRender ? (
                                                                                            <img src={altRender} alt={alt.name} className="w-full h-full object-cover object-top" loading="lazy" />
                                                                                        ) : (
                                                                                            <div className="w-full h-full flex items-center justify-center text-xs font-bold" style={{ color: tc(altColor) }}>{alt.name.charAt(0)}</div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="text-left">
                                                                                        <div className="flex items-center gap-1">
                                                                                            <span className="text-xs font-medium" style={{ color: tc(isActive ? altColor : `${altColor}90`) }}>{alt.name}</span>
                                                                                            {altWcl && <span className="text-[10px] font-bold" style={{ color: tc(getParseColor(altWcl)) }}>{altWcl}</span>}
                                                                                        </div>
                                                                                        <div className="text-[10px] text-[var(--c-ink-4)]">{altSpec || altClass}{alt.totalRaidCount ? ` · ${alt.totalRaidCount}r` : ''}</div>
                                                                                    </div>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Dual-Week Availability — visible to all members */}
                                                            <DualWeekAvailGrid currentWeekAvail={currentAvail} nextWeekAvail={nextAvail} userTz={userTz} />

                                                            {/* Management — Officers/Owners only */}
                                                            {(activeGuild.isOwner || activeGuild.isOfficer) && (
                                                                <div className="pt-3 border-t border-[var(--c-line-soft)]">
                                                                    <div className="text-[10px] uppercase font-bold text-[var(--c-ink-4)] tracking-widest mb-2.5">Manage</div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {player.allChars.map(c => {
                                                                            const isLoading = memberActionLoading === c.characterId;
                                                                            const cColor = getClassColor(c.metadata?.class_name || '', fields) || '#888';
                                                                            return (
                                                                                <div key={c.characterId} className="flex items-center gap-0 rounded-lg border border-[var(--c-line)] overflow-hidden bg-[var(--c-bg-veil)]">
                                                                                    <span className="px-2.5 py-1.5 text-xs font-semibold border-r border-[var(--c-line-soft)]" style={{ color: tc(cColor) }}>{c.name}</span>
                                                                                    {activeGuild.isOwner && !c.isOfficer && (
                                                                                        <button onClick={(e) => { e.stopPropagation(); handleToggleOfficer(c.characterId, false); }} disabled={isLoading}
                                                                                            className="px-2 py-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 border-r border-[var(--c-line-soft)] disabled:opacity-50 transition-colors"
                                                                                            title="Promote to Officer">
                                                                                            {isLoading ? '···' : '↑ Officer'}
                                                                                        </button>
                                                                                    )}
                                                                                    {activeGuild.isOwner && c.isOfficer && (
                                                                                        <button onClick={(e) => { e.stopPropagation(); handleToggleOfficer(c.characterId, true); }} disabled={isLoading}
                                                                                            className="px-2 py-1.5 text-[10px] font-bold text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] border-r border-[var(--c-line-soft)] disabled:opacity-50 transition-colors"
                                                                                            title="Demote from Officer">
                                                                                            {isLoading ? '···' : '↓ Demote'}
                                                                                        </button>
                                                                                    )}
                                                                                    <button onClick={(e) => { e.stopPropagation(); handleRemoveMember(c.characterId, c.name); }} disabled={isLoading}
                                                                                        className="px-2 py-1.5 text-[10px] font-bold text-red-600/60 dark:text-red-400/60 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                                                                                        title={`Remove ${c.name}`}>
                                                                                        {isLoading ? '···' : '✕'}
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                    </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    </div>
                                ) : (
                                    <div className="py-20 text-center rounded-xl border border-dashed border-[var(--c-line-soft)]">
                                        <div className="text-4xl mb-3 opacity-10">⚔️</div>
                                        <div className="text-[var(--c-ink-4)] text-sm">
                                            {rosterSearch.trim() ? 'No members match your search.' : 'No members yet.'}
                                        </div>
                                    </div>
                                )}

                                {/* Pagination controls */}
                                {totalRosterPages > 1 && (
                                    <div className="flex items-center justify-center gap-3 pt-6">
                                        <button
                                            onClick={() => setRosterPage(p => Math.max(0, p - 1))}
                                            disabled={rosterPage === 0}
                                            className="px-3 py-1.5 rounded-lg border border-[var(--c-line)] text-xs font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:border-[var(--c-line-strong)] disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                        >
                                            ← Prev
                                        </button>
                                        <div className="flex items-center gap-1.5">
                                            {Array.from({ length: totalRosterPages }, (_, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => setRosterPage(i)}
                                                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                                                        i === rosterPage
                                                            ? 'bg-[var(--c-sunken-2)] text-[var(--c-ink)] border border-[var(--c-line-strong)]'
                                                            : 'text-[var(--c-ink-4)] hover:text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]'
                                                    }`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => setRosterPage(p => Math.min(totalRosterPages - 1, p + 1))}
                                            disabled={rosterPage >= totalRosterPages - 1}
                                            className="px-3 py-1.5 rounded-lg border border-[var(--c-line)] text-xs font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:border-[var(--c-line-strong)] disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                        </section>
                    </div>

                    
                </>
                )}
            </div>
        ) : <div className="text-[var(--c-ink-3)]">Guild not found.</div>
      )}

      {isModalOpen && (
        <GuildFormModal
          key={modalMode === "create" ? "create" : activeGuild?.id || "edit"}
          mode={modalMode}
          games={games}
          fieldsByGame={fieldsByGame}
          onFieldsLoaded={handleFieldsLoaded}
          guild={modalMode === "edit" ? activeGuild || null : null}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateOrUpdate}
          onDelete={modalMode === "edit" ? handleDelete : undefined}
        />
      )}

      {/* Request-to-Join shortcut modal (launched from directory cards) */}
      {requestModalGuild && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setRequestModalGuild(null)} />
          <div className="relative w-full max-w-md bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-[var(--c-line)] bg-[var(--c-bg-veil)] flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--c-surface)] border border-[var(--c-line)] overflow-hidden flex items-center justify-center flex-shrink-0">
                {requestModalGuild.logoUrl ? (
                  <img
                    src={requestModalGuild.logoUrl.startsWith("http") ? requestModalGuild.logoUrl : `${AUTH_BASE_URL}${requestModalGuild.logoUrl}`}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-black text-[var(--c-ink-4)]">{requestModalGuild.name.charAt(0)}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[var(--c-ink)] truncate">{requestModalGuild.name}</div>
                <div className="text-[11px] text-[var(--c-ink-3)] truncate">
                  {requestModalGuild.serverName || "Any realm"}
                  {requestModalGuild.faction ? ` · ${requestModalGuild.faction}` : ""}
                </div>
              </div>
              <button onClick={() => setRequestModalGuild(null)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0">✕</button>
            </div>
            <div className="p-4">
              <RequestToJoinPanel
                guild={requestModalGuild}
                characters={characters}
                fields={fieldsByGame[requestModalGuild.gameId] || []}
                className="border-0 bg-transparent p-0"
                hideWhenEmpty={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* One-time guided setup after a guild is created: join link, Discord
          bot, then recruiting. Renders nothing unless it has been queued. */}
      <GuildSetupTour
        openGuildId={viewMode === "detail" ? selectedGuildId : null}
        stage={viewMode === "detail" ? detailTab : null}
        onOpenGuild={(gid) => { setSelectedGuildId(gid); setDetailTab("info"); setViewMode("detail"); }}
        onRequestStage={handleTourStage}
      />

    </div>
  );
}
